/* eslint-disable promise/avoid-new */
import type { Agent } from '@studio/models/agent';
import type { Room } from '@studio/models/room';
import { track } from '#~/analytics/track.js';
import { createBridgeLifecycleTracker } from '#~/analytics/domains/bridge-lifecycle.js';
import { depot } from '#~/depot/index.js';
import { logger } from '#~/logger/index.js';
import { clear } from '#~/notifications/index.js';
import { bridgeUnreachableNotificationId, postBridgeUnreachable } from './bridge-unreachable.js';
import { runStartSignalLoop } from './start-signal-policy.js';
import { settleStartSignalUnreachable } from './start-signal-terminal.js';
import { sendSignal } from './utils.js';

// START settles accepted, cancelled, or failed with Retry.
// The episode deadline also bounds a raw request that never returns.
export type StartResult =
  | { status: 'accepted' }
  | { status: 'cancelled' }
  | { status: 'failed'; cause: unknown };

type Caller = { attempt: Attempt; signal: AbortSignal | undefined; cleanup: () => void };

type Attempt = {
  agentId: Agent['id'];
  roomId: Room['id'];
  accountId: string | undefined;
  promise: Promise<StartResult>;
  resolve: (result: StartResult) => void;
  result?: StartResult;
  // Per-call 10 s bound on a fresh raw attempt.
  timer?: ReturnType<typeof setTimeout>;
  // Retiring the attempt cancels its backoff sleep.
  sleepTimer?: ReturnType<typeof setTimeout>;
  sleepResolve?: () => void;
  deadline: ReturnType<typeof setTimeout>;
  callers: Set<Caller>;
};

// Broker, idle detection and presence share START, including raw work from a retired caller.
const attempts = new Map<Agent['id'], Attempt>();
const rawRequests = new Map<Agent['id'], Promise<void>>();
let quitting = false;
let observing = false;
const current = (attempt: Attempt): boolean =>
  !quitting &&
  attempts.get(attempt.agentId) === attempt &&
  depot.getState().user?.id === attempt.accountId;

// Observer failures must not strand waiters or stop retries.
const observe = (callback: () => void): void => {
  try {
    callback();
  } catch {
    /* Settlement must survive observer failures. */
  }
};

// START acknowledgement may advance embedded idle to booting, but cannot establish readiness.
const bootEmbedded = (agentId: Agent['id']): void =>
  observe(() =>
    depot.getState().produce((draft) => {
      const agent = draft.agents[agentId];
      if (agent?.embedded && agent.status === 'idle') agent.status = 'booting';
    })
  );

// Retirement clears backoff and lets the policy loop unwind as cancelled.
const cancelSleep = (attempt: Attempt): void => {
  clearTimeout(attempt.sleepTimer);
  delete attempt.sleepTimer;
  const resolve = attempt.sleepResolve;
  delete attempt.sleepResolve;
  resolve?.();
};

const finish = (attempt: Attempt, result: StartResult): void => {
  if (attempt.result) return;
  attempt.result = result;
  clearTimeout(attempt.timer);
  clearTimeout(attempt.deadline);
  cancelSleep(attempt);
  attempt.resolve(result);
  if (!current(attempt) || result.status === 'cancelled') return;
  if (result.status === 'accepted') {
    bootEmbedded(attempt.agentId);
    // A successful START clears any standing terminal notice for this agent.
    observe(() => clear(bridgeUnreachableNotificationId(attempt.agentId)));
    return;
  }
  // Failure releases the raw reservation so explicit Retry sends a fresh command.
  // Cancellation keeps pending work adoptable; its late acknowledgement can still clear the notice.
  rawRequests.delete(attempt.agentId);
};

// Report one terminal failure without writing the agent offline. Retry re-arms this owner.
const settleUnreachable = (attempt: Attempt, error: unknown): void => {
  if (attempt.result) return;
  settleStartSignalUnreachable(error, {
    captureError: (captured) => observe(() => logger.error(captured)),
    // Skip terminal UI if a newer status already advanced the agent.
    markUnreachable: () => {
      if (!current(attempt)) return false;
      const status = depot.getState().agents[attempt.agentId]?.status;
      return status !== 'booting' && status !== 'online';
    },
    trackUnavailable: (properties) =>
      observe(() =>
        createBridgeLifecycleTracker((name, category, props) => {
          // Explicit identity fields follow optional data so static producer analysis can resolve them.
          void track({ ...(props ? { properties: props } : {}), name, category }).catch(
            () => undefined
          );
        }).trackBridgeConnectionUnavailable({
          agentId: attempt.agentId,
          studioId: attempt.roomId,
          cause: properties.cause,
          error_message: properties.error_message
        })
      ),
    postTerminal: () =>
      observe(() =>
        postBridgeUnreachable({
          key: attempt.agentId,
          onRetry: () => {
            // An obsolete Retry after room/account teardown must not revive work.
            if (!current(attempt)) return;
            void startAgent(
              attempt.agentId,
              attempt.roomId,
              [...attempt.callers].find((caller) => !caller.signal?.aborted)?.signal,
              true
            );
          }
        })
      )
  });
  finish(attempt, { status: 'failed', cause: error });
};

/** Retire only the matching room's START, leaving other agents and sources alone. */
export const retireAgentStart = (agentId: Agent['id'], roomId: Room['id']): void => {
  const attempt = attempts.get(agentId);
  if (!attempt || attempt.roomId !== roomId) return;
  finish(attempt, { status: 'cancelled' });
  attempts.delete(agentId);
  for (const caller of attempt.callers) caller.cleanup();
  attempt.callers.clear();
  observe(() => clear(bridgeUnreachableNotificationId(agentId)));
};

/** Retire the room even if its idle agents already advanced to booting. */
export const retireRoomStarts = (roomId: Room['id']): void => {
  for (const attempt of attempts.values()) {
    if (attempt.roomId === roomId) retireAgentStart(attempt.agentId, roomId);
  }
};

/** Called synchronously on the existing exit IPC path, before route teardown. */
export const stopAgentStarts = (): void => {
  quitting = true;
  for (const attempt of attempts.values()) retireAgentStart(attempt.agentId, attempt.roomId);
};

// Share a reserved raw request across episodes to prevent duplicate commands.
// Fresh calls have a 10s bound; the 65s episode deadline bounds adoption of a hung request.
const sendAttempt = (attempt: Attempt): Promise<void> => {
  const pending = rawRequests.get(attempt.agentId);
  if (pending) return pending;
  const raw = Promise.resolve().then(() => {
    if (!current(attempt) || attempt.result) return undefined;
    return sendSignal(attempt.agentId);
  });
  rawRequests.set(attempt.agentId, raw);
  void raw.then(
    () => {
      if (rawRequests.get(attempt.agentId) === raw) rawRequests.delete(attempt.agentId);
      // A late accepted START clears a stale failure notice and boots a still-idle embedded agent.
      if (current(attempt) && attempt.result?.status === 'failed') {
        observe(() => clear(bridgeUnreachableNotificationId(attempt.agentId)));
        bootEmbedded(attempt.agentId);
      }
    },
    () => {
      if (rawRequests.get(attempt.agentId) === raw) rawRequests.delete(attempt.agentId);
    }
  );
  return new Promise<void>((resolve, reject) => {
    attempt.timer = setTimeout(() => reject(new Error('START acknowledgement timed out.')), 10_000);
    void raw.then(resolve, reject).finally(() => clearTimeout(attempt.timer));
  });
};

const run = (attempt: Attempt): void => {
  if (!current(attempt) || attempt.result) return;
  // The policy loop owns retry timing; this layer owns transport and caller lifetimes.
  void runStartSignalLoop({
    sendSignal: () => sendAttempt(attempt),
    onBooting: () => observe(() => finish(attempt, { status: 'accepted' })),
    // Expected boot-race failures are warnings; only exhaustion is captured.
    onTransientFailure: (error) =>
      observe(() => logger.warning('START attempt failed; retrying.', error as Error)),
    onUnreachable: (error) => observe(() => settleUnreachable(attempt, error)),
    sleep: (ms) =>
      new Promise<void>((resolve) => {
        cancelSleep(attempt);
        attempt.sleepResolve = resolve;
        attempt.sleepTimer = setTimeout(() => {
          delete attempt.sleepResolve;
          resolve();
        }, ms);
      }),
    isCancelled: () => !current(attempt) || attempt.result !== undefined
  });
};

/** Join the room/account's START episode; Retry only replaces a settled episode. */
export const startAgent = (
  agentId: Agent['id'],
  roomId: Room['id'],
  signal?: AbortSignal,
  retry = false
): Promise<StartResult> => {
  if (quitting || signal?.aborted) return Promise.resolve({ status: 'cancelled' });
  if (!observing) {
    observing = true;
    depot.subscribe((state, previous) => {
      for (const attempt of attempts.values()) {
        if (
          state.user?.id !== attempt.accountId ||
          (previous.agents[attempt.agentId] !== undefined &&
            state.agents[attempt.agentId] === undefined) ||
          state.agents[attempt.agentId]?.status === 'online' ||
          // Only a non-idle → idle transition starts a new incident; peer updates leave work intact.
          (state.agents[attempt.agentId]?.status === 'idle' &&
            previous.agents[attempt.agentId] !== undefined &&
            previous.agents[attempt.agentId]?.status !== 'idle')
        ) {
          retireAgentStart(attempt.agentId, attempt.roomId);
        } else if (
          state.agents[attempt.agentId]?.status === 'booting' &&
          previous.agents[attempt.agentId]?.status !== 'booting'
        ) {
          finish(attempt, { status: 'cancelled' });
        }
      }
    });
  }
  let attempt = attempts.get(agentId);
  const accountId = depot.getState().user?.id;
  let survivors: Attempt['callers'] | undefined;
  if (
    attempt &&
    (attempt.roomId !== roomId ||
      attempt.accountId !== accountId ||
      (retry && attempt.result && attempt.result.status !== 'accepted'))
  ) {
    // Promise settlement ends the wait, not the mounted caller's lifetime.
    // Deliberate Retry carries every surviving lifetime into the next episode.
    if (retry && attempt.roomId === roomId && attempt.accountId === accountId) {
      survivors = attempt.callers;
      attempt.callers = new Set();
    }
    retireAgentStart(agentId, attempt.roomId);
    attempt = undefined;
  }
  if (!attempt) {
    let resolve!: Attempt['resolve'];
    const promise = new Promise<StartResult>((done) => {
      resolve = done;
    });
    const created: Attempt = {
      agentId,
      roomId,
      accountId,
      promise,
      resolve,
      callers: survivors ?? new Set(),
      // Bound a hung raw request with the same Retry terminal as exhausted attempts.
      deadline: setTimeout(
        () => settleUnreachable(created, new Error('START retry budget exhausted.')),
        65_000
      )
    };
    attempt = created;
    for (const caller of created.callers) caller.attempt = created;
    attempts.set(agentId, attempt);
    void Promise.resolve().then(() => run(created));
  }
  const joined = attempt;
  return new Promise((resolve) => {
    let settled = false;
    const settle = (result: StartResult): void => {
      if (settled) return;
      settled = true;
      resolve(result);
    };
    const caller: Caller = {
      attempt: joined,
      signal,
      cleanup: () => signal?.removeEventListener('abort', cancel)
    };
    const cancel = (): void => {
      settle({ status: 'cancelled' });
      const owner = caller.attempt;
      owner.callers.delete(caller);
      caller.cleanup();
      if (!owner.callers.size && current(owner)) retireAgentStart(agentId, roomId);
    };
    joined.callers.add(caller);
    signal?.addEventListener('abort', cancel, { once: true });
    void joined.promise.then(settle);
  });
};
